import {useState, useEffect, useRef} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import Message from "../message/Message";
import updatePost from "../../api/updatePost";
import {EditPostModalProps} from "../../types/ComponentProps";
import EditPostModalStyles from "./EditPostModalStyles.module.css";
import useGetPostById from "../../hooks/useGetPostById";
import Spinner from "../spinner/Spinner";
import useGetAllPostsById from "../../hooks/useGetAllPostsById";

type FormPostUpdateProps = {
  message?: string;
  imagePost?: File[];
  imageURLs?: string[];
};

const EditPostModal = ({
  setEditPostModal,
  editCardSelection,
}: EditPostModalProps) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const {getPostByIdData, isLoading} = useGetPostById(editCardSelection ?? 0);
  const [onUpdateLoading, setOnUpdateLoading] = useState<boolean>(false);
  const [imageOnBackEnd, setImageOnBackend] = useState<string[]>([]);
  const [loadedImages, setLoadedImages] = useState<string[]>([]);

  const handleImageLoad = (url: string) => {
    setLoadedImages((prev) => [...prev, url]);
  };

  const {refetchGetAllPostsByIdData} = useGetAllPostsById(
    editCardSelection ?? 0
  );

  const [formPost, setFormPost] = useState<FormPostUpdateProps>({
    message: "",
    imagePost: [],
  });

  useEffect(() => {
    if (getPostByIdData?.post?.message && getPostByIdData?.post?.imageURL) {
      setFormPost({
        message: getPostByIdData.post.message,
        imageURLs: getPostByIdData.post.imageURL,
      });
      setImageOnBackend(getPostByIdData.post.imageURL || []);
    }
  }, [getPostByIdData]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const updatePostMutation = useMutation({
    mutationFn: () => updatePost(Number(editCardSelection ?? 0), formPost),
    onSuccess: async () => {
      try {
        await queryClient.invalidateQueries({
          queryKey: ["getAllPosts"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["getPostById"],
        });
      } catch (error: any) {
        throw new Error(error);
      } finally {
        if (setEditPostModal && setOnUpdateLoading) {
          await queryClient.invalidateQueries({
            queryKey: ["getAllPostsById"],
          });
          await refetchGetAllPostsByIdData();
          setOnUpdateLoading(false);
          setEditPostModal(false);
        }
      }
    },
    onError: (error: Error) => {
      console.error("Error create database:", error);
    },
  });

  const onCloseClick = () => {
    if (setEditPostModal) {
      setEditPostModal(false);
    }
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const {name, value} = e.target;
    setFormPost((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onUpdateSubmitHandler = () => {
    setOnUpdateLoading(true);
    updatePostMutation.mutate();
  };

  // Image Handlers
  const imageUploadHandler = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
      const newFiles = Array.from(files).filter((file) =>
        validImageTypes.includes(file.type)
      );

      const totalExisting =
        (formPost.imagePost?.length || 0) + (formPost.imageURLs?.length || 0);

      const totalAfterUpload = totalExisting + newFiles.length;

      if (totalAfterUpload > 10) {
        setMessage("You can upload a maximum of 10 images");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      const previews = newFiles.map((file) => URL.createObjectURL(file));

      setFormPost((previous) => ({
        ...previous,
        imagePost: [...(previous.imagePost || []), ...newFiles],
      }));
      setImageOnBackend((prev) => [...prev, ...previews]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeImageBlk = (urlToRemove: string) => {
    const updatedImages = imageOnBackEnd.filter((url) => url !== urlToRemove);
    setImageOnBackend(updatedImages);
    setFormPost((prev) => ({
      ...prev,
      imageURLs: updatedImages,
    }));
  };

  if (isLoading) {
    return (
      <div className={EditPostModalStyles.updatePostModalContainer}>
        <div className={EditPostModalStyles.updatePostModalMain}>
          <Spinner variant="medium-spinner" alignSpin="center-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className={EditPostModalStyles.updatePostModalContainer}>
      <div className={EditPostModalStyles.updatePostModalMain}>
        <i className="fa-solid fa-xmark" onClick={onCloseClick}></i>
        <div>
          <h1>Edit Post</h1>
        </div>
        <textarea
          className={EditPostModalStyles.textAreaSectionBlk}
          name="message"
          onChange={onChangeHandler}
          value={formPost.message}
          placeholder={getPostByIdData?.post?.message || "Enter your message"}
          spellCheck="false"
          maxLength={1024}
        />
        {imageOnBackEnd.length > 0 && (
          <div className={EditPostModalStyles.imageSectionEditPost}>
            <div className={EditPostModalStyles.imageSectionEditPostFlexBox}>
              {imageOnBackEnd.map((data, index) => (
                <div
                  key={index}
                  className={EditPostModalStyles.previewImageCointainerBlk}
                  style={{position: "relative"}}>
                  {/* Show spinner only while not loaded */}
                  {!loadedImages.includes(data) && (
                    <div className={EditPostModalStyles.imageLoadingSpinner}>
                      <Spinner
                        alignSpin="center-spinner"
                        variant="extra-small-spinner"
                      />
                    </div>
                  )}

                  <img
                    src={data}
                    alt="uploaded"
                    onLoad={() => handleImageLoad(data)}
                    style={{
                      opacity: loadedImages.includes(data) ? 1 : 0,
                      transition: "opacity 0.3s ease-in-out",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />

                  {loadedImages.includes(data) && (
                    <div
                      className={EditPostModalStyles.removeIconBlk}
                      onClick={() => removeImageBlk(data)}
                      style={{zIndex: 2}}>
                      <span className="fa-solid fa-xmark"></span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <hr />

        {message && <Message variant="error">{message}</Message>}

        <div className={EditPostModalStyles.footerBlkUpdateSection}>
          <div>
            <div className={EditPostModalStyles.footerBlkUpdateSectionIcons}>
              <div
                className={EditPostModalStyles.imageButtonContainer}
                onClick={imageUploadHandler}>
                <span className="fa-regular fa-image"></span>
                <p>{imageOnBackEnd ? "Upload new photo" : "upload photo"}</p>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                name="imagePost"
                style={{display: "none"}}
                onChange={handleFileChange}
                multiple
              />
            </div>
          </div>
          <div>
            <button onClick={onUpdateSubmitHandler}>
              {onUpdateLoading ? (
                <Spinner
                  variant="extra-small-spinner"
                  alignSpin="center-spinner"
                />
              ) : (
                "Update Post"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPostModal;
